import { useEffect, useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import { useDayPlanMultiWeek } from "../../hooks/useDayPlanWeek";
import { formatEasternClockRange } from "../../lib/eastern-time-format";
import { useBackend } from "../../lib/backend";
import type {
  PerformanceStrategyDraft,
  PerformanceStrategyInput,
} from "../../lib/services/performance-strategy";
import type { EconForecast } from "../../types/day-plan";
import { buildQueuedDeskEvents } from "../desk/desk-queued-events";
import { FadingRuler } from "../shared/FadingRuler";

interface StrategyState {
  status: "idle" | "loading" | "ready" | "unavailable";
  draft: PerformanceStrategyDraft | null;
  message: string | null;
  attempt: number;
}

const INITIAL_STRATEGY_STATE: StrategyState = {
  status: "idle",
  draft: null,
  message: null,
  attempt: 0,
};

export function UpcomingBulletinTab() {
  const backend = useBackend();
  const { allPlans, isLoading, error } = useDayPlanMultiWeek();
  const [strategyState, setStrategyState] = useState(INITIAL_STRATEGY_STATE);
  const nextEvent = useMemo(() => findNextEvent(allPlans), [allPlans]);

  useEffect(() => {
    if (!nextEvent) return;
    let cancelled = false;
    async function loadStrategy() {
      setStrategyState((state) => ({
        ...state,
        status: "loading",
        draft: null,
        message: null,
      }));
      try {
        const context = await backend.performanceStrategy.getStrategyContext();
        const input: PerformanceStrategyInput = {
          ...context.defaults,
          session: sessionFromTime(nextEvent!.window.startTime),
          riskPosture: "balanced",
        };
        const draft =
          await backend.performanceStrategy.createStrategyDraft(input);
        if (cancelled) return;
        if (draft.edgeful.sourceState !== "live") {
          setStrategyState((state) => ({
            ...state,
            status: "unavailable",
            draft,
            message: edgefulStateMessage(draft),
          }));
          return;
        }
        setStrategyState((state) => ({
          ...state,
          status: "ready",
          draft,
          message: null,
        }));
      } catch {
        if (cancelled) return;
        setStrategyState((state) => ({
          ...state,
          status: "unavailable",
          draft: null,
          message: "EdgeCaster strategy unavailable.",
        }));
      }
    }
    void loadStrategy();
    return () => {
      cancelled = true;
    };
  }, [backend.performanceStrategy, nextEvent?.id, strategyState.attempt]);

  if (isLoading) return <BulletinState text="Loading the next Desk event..." />;
  if (error && !nextEvent)
    return (
      <BulletinState text="Upcoming Desk event unavailable." tone="error" />
    );
  if (!nextEvent)
    return <BulletinState text="No future Desk Plan event is scheduled." />;

  const forecast = nextEvent.window.econForecast;
  const strategy = strategyCopy(strategyState);
  const invalidation =
    strategyState.status === "ready"
      ? strategyState.draft?.invalidation
      : forecast?.whatInvalidates;

  return (
    <section
      className="space-y-4 animate-in fade-in duration-150"
      data-bulletin-upcoming
    >
      <div>
        <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--fintheon-muted)]">
          Next volatile event
        </p>
        <div className="mt-2 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-[14px] font-semibold leading-snug text-[var(--fintheon-text)]">
              {nextEvent.title}
            </h3>
            <p className="mt-1 font-mono text-[9px] tabular-nums text-[var(--fintheon-accent)]">
              {nextEvent.dateLabel} /{" "}
              {formatEasternClockRange(
                nextEvent.window.startTime,
                nextEvent.window.endTime,
              )}{" "}
              ET
            </p>
          </div>
          <span className="shrink-0 font-mono text-[9px] uppercase text-[var(--fintheon-muted)]">
            {nextEvent.country}
          </span>
        </div>
      </div>

      <FadingRuler className="opacity-45" />
      <DecisionRow
        label="EdgeCaster strategy"
        value={strategy.value}
        detail={strategy.detail}
      />
      <DecisionRow
        label="Bias"
        value={formatBias(forecast)}
        detail={forecast?.aiPrediction ?? "No stored Desk bias note."}
      />
      <DecisionRow
        label="Invalidation"
        value={invalidation?.trim() || "Unavailable"}
        detail={
          invalidation
            ? "Stored with the active strategy or Desk forecast."
            : "No invalidation price or rule is stored for this event."
        }
      />

      {strategyState.status === "unavailable" ? (
        <button
          type="button"
          onClick={() =>
            setStrategyState((state) => ({
              ...state,
              attempt: state.attempt + 1,
            }))
          }
          className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.12em] text-[var(--fintheon-accent)] transition-opacity hover:opacity-75"
        >
          <RefreshCw className="h-3 w-3" /> Retry strategy
        </button>
      ) : null}
    </section>
  );
}

function DecisionRow({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div className="grid grid-cols-[116px_minmax(0,1fr)] gap-4">
      <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-[var(--fintheon-muted)]">
        {label}
      </span>
      <div className="min-w-0 text-right">
        <p className="text-[12px] font-medium leading-snug text-[var(--fintheon-text)]">
          {value}
        </p>
        <p className="mt-1 text-[9px] leading-relaxed text-[var(--fintheon-muted)]">
          {detail}
        </p>
      </div>
    </div>
  );
}

function BulletinState({
  text,
  tone = "neutral",
}: {
  text: string;
  tone?: "neutral" | "error";
}) {
  return (
    <p
      className={`py-8 text-center text-[11px] ${tone === "error" ? "text-[var(--fintheon-bearish)]" : "text-[var(--fintheon-muted)]"}`}
    >
      {text}
    </p>
  );
}

function findNextEvent(plans: Parameters<typeof buildQueuedDeskEvents>[0]) {
  const nowKey = currentEasternMinuteKey();
  return buildQueuedDeskEvents(plans).find(
    (event) => `${event.date} ${event.window.endTime}` >= nowKey,
  );
}

function currentEasternMinuteKey() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const get = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}`;
}

function sessionFromTime(time: string): PerformanceStrategyInput["session"] {
  const hour = Number(time.slice(0, 2));
  if (hour >= 7 && hour < 17) return "ny";
  if (hour >= 2 && hour < 7) return "london";
  return "asia";
}

function formatBias(forecast: EconForecast | null) {
  if (!forecast) return "Unavailable";
  if (forecast.beat.probability === forecast.miss.probability) return "Neutral";
  const favored =
    forecast.beat.probability > forecast.miss.probability
      ? forecast.beat
      : forecast.miss;
  return `${favored.isBullishForEquities ? "Bullish" : "Bearish"} / ${favored.probability}%`;
}

function strategyCopy(state: StrategyState) {
  if (state.status === "loading")
    return {
      value: "Loading",
      detail: "Matching the event session to live Edgeful data.",
    };
  if (state.status === "ready" && state.draft)
    return {
      value:
        state.draft.edgeful.reportName || labelSetup(state.draft.input.setup),
      detail: `${state.draft.input.instrument} / ${state.draft.edgeful.sessionWindow.label}`,
    };
  return {
    value: "Unavailable",
    detail: state.message ?? "No live Edgeful strategy was returned.",
  };
}

function edgefulStateMessage(draft: PerformanceStrategyDraft) {
  const label = draft.edgeful.sourceState.replace(/_/g, " ");
  return draft.edgeful.error || `Edgeful source is ${label}.`;
}

function labelSetup(value: string) {
  return value
    .split("-")
    .map((part) => `${part[0]?.toUpperCase() ?? ""}${part.slice(1)}`)
    .join(" ");
}
